<template>
  <v-row>
    <v-col>
      <v-card class="mb-3" v-if="miqaat">
        <v-card-text>

          <v-row>
            <v-col cols="12" md="10">
              <h6 class="text-h6 font-weight-bold">{{ miqaat.miqaatName }}</h6>
              
              <p class="text-body-1 mb-0 mt-2">
                Event: <b>{{ moment(miqaat.startDate).format("D-MMM-yyyy") }}</b> to <b>{{
                  moment(miqaat.endDate).format("D-MMM-yyyy") }}</b><br />
                Registration: <b>{{ moment(miqaat.registrationStartDate).format("D-MMM-yyyy") }}</b> to <b>{{
                  moment(miqaat.registrationEndDate).format("D-MMM-yyyy") }}</b><br />
                Registration Amount: <strong>{{ miqaat.registrationCommitmentAmount }}</strong>
              </p>
              
              <div v-if="miqaat.description" class="text-body-1 mt-3" v-html="miqaat.description" />
            </v-col>
            <v-col cols="12" md="2" align="end">
              <v-btn
                v-if="registrationOpen"
                color="primary"
                class="mt-2"
                @click="openRegisterMember"
              >
                Register Member
              </v-btn>
            </v-col>
          </v-row>

        </v-card-text>
      </v-card>

      <v-card class="elevation-2 rounded">
        <v-table density="compact">
          <thead>
            <tr>
              <th style="width: 40px"></th>
              <th>#</th>
              <th>ITS ID</th>
              <th>Full Name</th>
              <th>Registration Amount</th>
              <th>Niaz Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <draggable
            v-model="registrations"
            tag="tbody"
            item-key="miqaatRegistrationId"
            handle=".row-drag"
            :move="onMove"
            @end="onDragEnd"
          >
            <template #item="{ element: item }">
              <tr>
                <td>
                  <v-icon class="row-drag" style="cursor: grab" size="small">mdi-drag</v-icon>
                </td>
                <td>{{ item.sequence }}</td>
                <td>{{ item.itsId }}</td>
                <td>{{ item.fullName }}</td>
                <td>{{ item.registrationCommitmentAmount }}</td>
                <td>{{ item.niazCommitment }}</td>
                <td>
                  <v-chip :color="statusColor(item.status)" size="small">{{ formatStatus(item.status) }}</v-chip>
                </td>
                <td>
                  <v-btn size="small" variant="text" class="mr-2" @click="viewDetail(item)">View</v-btn>
                  <v-btn
                    v-if="item.status === 'PendingIncharge'"
                    size="small"
                    color="success"
                    variant="text"
                    class="mr-2"
                    @click="approve(item)"
                  >
                    Approve
                  </v-btn>
                  <v-btn
                    v-if="item.status === 'PendingIncharge'"
                    size="small"
                    color="error"
                    variant="text"
                    @click="reject(item)"
                  >
                    Reject
                  </v-btn>
                </td>
              </tr>
            </template>
          </draggable>
        </v-table>
        <div v-if="registrations.length === 0" class="text-center text-medium-emphasis pa-4">
          No Records Available
        </div>
      </v-card>
    </v-col>
  </v-row>

  <MiqaatRegistrationDetailDialog ref="dlgDetail" />
  <MiqaatRegisterMemberDialog v-if="miqaat" ref="dlgRegisterMember" mode="incharge" :miqaat-id="miqaatId"
    :registration-commitment-amount="miqaat.registrationCommitmentAmount"
    :niaz-commitment-required="miqaat.niazCommitmentRequired !== false"
    :niaz-commitment-compulsory="miqaat.niazCommitmentCompulsory !== false"
    @registered="loadRegistrations" />
</template>

<script setup>
import { ref, inject, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import moment from "moment";
import draggable from "vuedraggable";
import {
  getAvailableForIncharge,
  getInchargeRegistrations,
  inchargeApprove,
  rejectRegistration,
  reorderRegistrations,
  isMiqaatRegistrationOpen,
} from "@/services/miqaatService";
import MiqaatRegistrationDetailDialog from "./components/MiqaatRegistrationDetailDialog.vue";
import MiqaatRegisterMemberDialog from "./components/MiqaatRegisterMemberDialog.vue";

const route = useRoute();
const router = useRouter();
const loader = inject("loader");
const snackbar = inject("snackBar");
const inputDialog = inject("inputDialog");

const miqaatId = computed(() => Number(route.params.miqaatId));
const miqaats = ref([]);
const registrations = ref([]);
const dlgDetail = ref(null);
const dlgRegisterMember = ref(null);

const miqaat = computed(() =>
  miqaats.value.find((m) => m.miqaatId === miqaatId.value) || null
);

const registrationOpen = computed(() => isMiqaatRegistrationOpen(miqaat.value));

function formatStatus(status) {
  const map = {
    PendingIncharge: "Pending Incharge",
    PendingCentral: "Pending Central",
    Approved: "Approved",
    Rejected: "Rejected",
  };
  return map[status] || status;
}

function statusColor(status) {
  const map = {
    PendingIncharge: "warning",
    PendingCentral: "info",
    Approved: "success",
    Rejected: "error",
  };
  return map[status] || "grey";
}

function onMove(evt) {
  const dragged = evt.draggedContext?.element;
  const related = evt.relatedContext?.element;
  if (!dragged || !related) return true;
  return dragged.organizationId === related.organizationId;
}

async function onDragEnd(evt) {
  if (evt.oldIndex == null || evt.newIndex == null || evt.oldIndex === evt.newIndex) return;

  const moved = registrations.value[evt.newIndex];
  if (!moved) return;

  const organizationId = moved.organizationId;
  const orderedRegistrationIds = registrations.value
    .filter((r) => r.organizationId === organizationId)
    .map((r) => r.miqaatRegistrationId);

  loader.show();
  try {
    await reorderRegistrations({
      miqaatId: miqaatId.value,
      organizationId,
      orderedRegistrationIds,
    });
    await loadRegistrations();
  } catch (error) {
    snackbar.error(error);
    await loadRegistrations();
  } finally {
    loader.hide();
  }
}

async function loadMiqaat() {
  try {
    miqaats.value = await getAvailableForIncharge();
    if (!miqaat.value) {
      snackbar.error("Miqaat is not available");
      router.push("/miqaat-management/incharge");
    }
  } catch (error) {
    snackbar.error(error);
  }
}

async function loadRegistrations() {
  loader.show();
  try {
    registrations.value = await getInchargeRegistrations(miqaatId.value);
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

async function viewDetail(item) {
  dlgDetail.value.show(item.miqaatRegistrationId);
}

function openRegisterMember() {
  dlgRegisterMember.value?.show();
}

async function approve(item) {
  loader.show();
  try {
    await inchargeApprove(item.miqaatRegistrationId);
    snackbar.success("Registration approved");
    await loadRegistrations();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

async function reject(item) {
  const answer = await inputDialog.input("Reject Registration", "Rejection reason (optional)");
  if (answer == null) return;

  loader.show();
  try {
    await rejectRegistration(item.miqaatRegistrationId, { rejectionReason: answer.answer });
    snackbar.success("Registration rejected");
    await loadRegistrations();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

onMounted(async () => {
  await loadMiqaat();
  await loadRegistrations();
});
</script>
